// src/pages/GamesPost.jsx
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import Subpage from "../components/Subpage";
import AnimatedButton from "../components/AnimatedButton";
import { API_BASE, mediaUrl } from "../utils/apiBase";
import { renderStrapiBlocks } from "../utils/renderStrapiBlocks";

/* ───────── helpers ───────── */
const firstImage = (sections = []) =>
  sections
    .flatMap((s) => s.Media || [])
    .map((m) => m?.url || m?.attributes?.url)
    .filter(Boolean)[0];

const extractYouTubeID = (url) =>
  url?.match(
    /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{11})/
  )?.[1] ?? null;

/* ───────── component ───────── */
export default function GamesPost() {
  const { slugOrId, documentId } = useParams();
  const param = slugOrId || documentId;
  const [game, setGame] = useState(null);

  /* fetch once */
  useEffect(() => {
    axios
      .get(
        `${API_BASE}/games` +
       "?populate[TeaserImage][populate]=*" +
       "&populate[Sections][populate]=Media" +
       `&filters[$or][0][slug][$eq]=${encodeURIComponent(param)}` +
        `&filters[$or][1][documentId][$eq]=${encodeURIComponent(param)}`
      )
      .then((res) => setGame(res.data.data[0]))
      .catch((err) => console.error("Error fetching game post:", err));
  }, [param]);

  if (!game)
    return (
      <Subpage title="Loading…" content="Please wait.">
        <p>Loading game details…</p>
      </Subpage>
    );

  const { Title, PublishDate, Links, Sections, TeaserImage } = game;

  /* banner image: prefer TeaserImage, else first section image */
  const bannerUrl =
    TeaserImage?.url ||
    TeaserImage?.[0]?.url || // sometimes Strapi returns an array
    firstImage(Sections);

  /* ───────── block renderer ───────── */
  const renderBlock = (block, i) => {
    const { TextContent, Media, YoutubeURL } = block;

    /* text */
    const text = Array.isArray(TextContent)
      ? renderStrapiBlocks(TextContent)
      : null;


    /* images */
    const images =
      Array.isArray(Media) && Media.length > 0 ? (
        <div className="mb-8 space-y-8">
          {Media.map((file, k) => {
            const url = file?.url || file?.attributes?.url;
            return url ? (
              <img
                key={k}
                src={mediaUrl(url)}
                alt={`Screenshot ${k + 1}`}
                className="w-full"
              />
            ) : (
              <p key={k} className="text-red-500">
                ⚠️ Missing image URL
              </p>
            );
          })}
        </div>
      ) : null;

    /* YouTube */
    const youtube = (() => {
      const id = extractYouTubeID(YoutubeURL);
      if (!id) return null;
      return (
        <div className="aspect-video mb-8">
          <iframe
            className="w-full h-full rounded-md"
            src={`https://www.youtube.com/embed/${id}`}
            title="YouTube video"
            allowFullScreen
          />
        </div>
      );
    })();

    return (
      <div key={i} className="mb-12">
        {text}
        {images}
        {youtube}
      </div>
    );
  };

  /* ───────── render ───────── */
  return (
    <Subpage title="" content="" relatedLinks={[]}>
      {/* Banner */}
      {bannerUrl && (
        <div className="relative w-screen left-1/2 -translate-x-1/2 h-96 mb-16 overflow-hidden">
          <img
            src={mediaUrl(bannerUrl)}
            alt={Title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-white/30 mix-blend-screen" />
          <div className="absolute inset-0 flex items-center justify-center">
            <h1 className="text-5xl md:text-6xl font-extrabold text-black drop-shadow-sm px-4 text-center">
              {Title}
            </h1>
          </div>
        </div>
      )}

      {/* Published date */}
      {PublishDate && (
        <p className="text-center text-sm text-gray-500 mb-12">
          Published&nbsp;
          {new Date(PublishDate).toLocaleDateString(undefined, {
            year: "numeric",
            month: "long",
            day: "numeric",
          })}
        </p>
      )}

      {/* Dynamic blocks */}
      <div className="max-w-3xl mx-auto">
        {Array.isArray(Sections) && Sections.map(renderBlock)}

        {Links && (
          <AnimatedButton
            text="Visit Game Site →"
            href={Links}
            type="link"
            className="mt-8"
          />
        )}
      </div>
    </Subpage>
  );
}
